// src/components/tutorials/JS_Keywords_Reserved.js
import React from "react";
import "../../../AllComponentCSS.css";

export default function JS_Keywords_Reserved() {
  const reserved = [
    { name: "await", desc: "Used with async functions to wait for a Promise" },
    { name: "break", desc: "Terminates a loop or switch statement" },
    { name: "case", desc: "Defines a case in a switch statement" },
    { name: "catch", desc: "Handles errors in a try...catch block" },
    { name: "class", desc: "Defines a class for creating objects" },
    { name: "const", desc: "Declares a block-scoped constant variable" },
    { name: "continue", desc: "Skips the current iteration of a loop" },
    { name: "debugger", desc: "Invokes debugging functionality" },
    { name: "default", desc: "Specifies a default block in switch or export" },
    { name: "delete", desc: "Deletes a property from an object" },
    { name: "do", desc: "Used with while to create a do...while loop" },
    {
      name: "else",
      desc: "Specifies a block of code to execute if a condition is false",
    },
    { name: "enum", desc: "Reserved for future use" },
    { name: "export", desc: "Exports modules, functions, or variables" },
    { name: "extends", desc: "Creates a subclass based on another class" },
    { name: "false", desc: "Boolean value representing false" },
    {
      name: "finally",
      desc: "Executes code after try/catch regardless of result",
    },
    {
      name: "for",
      desc: "Creates a loop that runs a block of code multiple times",
    },
    { name: "function", desc: "Declares a function" },
    { name: "if", desc: "Executes a block of code if a condition is true" },
    { name: "import", desc: "Imports functions or modules" },
    { name: "in", desc: "Checks if a property exists in an object" },
    {
      name: "instanceof",
      desc: "Checks if an object is an instance of a class",
    },
    { name: "new", desc: "Creates an instance of an object" },
    { name: "null", desc: "Represents the null value" },
    { name: "return", desc: "Exits a function and optionally returns a value" },
    {
      name: "super",
      desc: "Refers to the parent class constructor or methods",
    },
    { name: "switch", desc: "Selects one of many code blocks to execute" },
    { name: "this", desc: "Refers to the current object context" },
    { name: "throw", desc: "Throws an exception (custom error)" },
    { name: "true", desc: "Boolean value representing true" },
    { name: "try", desc: "Marks a block to test for errors" },
    { name: "typeof", desc: "Returns the type of a variable or expression" },
    { name: "var", desc: "Declares a function-scoped variable" },
    { name: "void", desc: "Evaluates an expression and returns undefined" },
    {
      name: "while",
      desc: "Creates a loop that runs while a condition is true",
    },
    {
      name: "with",
      desc: "Extends the scope chain for a statement (deprecated)",
    },
    { name: "yield", desc: "Pauses and resumes a generator function" },
  ];

  const futureReserved = [
    {
      name: "implements",
      desc: "Reserved for future use (class implementation)",
    },
    {
      name: "interface",
      desc: "Reserved for future use (interface definitions)",
    },
    {
      name: "let",
      desc: "Reserved in older JS versions, now used for block scope",
    },
    { name: "package", desc: "Reserved for future use (modules/packages)" },
    { name: "private", desc: "Reserved for future use (private class fields)" },
    { name: "protected", desc: "Reserved for future use (protected members)" },
    { name: "public", desc: "Reserved for future use (public members)" },
    {
      name: "static",
      desc: "Reserved for future use (static members in classes)",
    },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">JavaScript Reserved Keywords</h1>
      <p className="tutorial-intro">Revised July 2025</p>

      {/* 🔹 Header */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          color: "#000",
          borderRadius: "12px",
          padding: "1rem 1.5rem",
        }}
      >
        <h2
          style={{ color: "#78350f", fontSize: "2rem", marginBottom: "0.5rem" }}
        >
          JavaScript Reserved Keywords Table
        </h2>
        <p style={{ fontSize: "1.1rem", margin: 0 }}>
          A complete list of reserved and future reserved keywords in
          JavaScript.
        </p>
      </div>

      {/* 🔹 Reserved Keywords Table */}
      <div
        className="reference-table-container"
        style={{
          marginTop: "1.5rem",
          overflowX: "auto",
          borderRadius: "10px",
          boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        }}
      >
        <table
          className="reference-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "1rem",
            backgroundColor: "#fff",
          }}
        >
          <thead style={{ background: "#0d9488", color: "white" }}>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: "0.8rem 1rem",
                  width: "25%",
                }}
              >
                Keyword
              </th>
              <th style={{ textAlign: "left", padding: "0.8rem 1rem" }}>
                Description
              </th>
            </tr>
          </thead>
          <tbody>
            {reserved.map((item, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "#f9fafb" : "#fff",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <td
                  style={{
                    padding: "0.7rem 1rem",
                    fontFamily: "monospace",
                    fontWeight: "bold",
                    color: "#2563eb",
                  }}
                >
                  {item.name}
                </td>
                <td style={{ padding: "0.7rem 1rem" }}>{item.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 🔹 Future Reserved Keywords Table */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #f0f9ff)",
          color: "#000",
          borderRadius: "12px",
          padding: "1rem 1.5rem",
          marginTop: "2rem",
        }}
      >
        <h2
          style={{
            color: "#075985",
            fontSize: "1.8rem",
            marginBottom: "0.5rem",
          }}
        >
          Future Reserved Keywords
        </h2>
        <p style={{ fontSize: "1.1rem", margin: 0 }}>
          These words are reserved for possible future JavaScript versions.
        </p>
      </div>

      <div
        className="reference-table-container"
        style={{
          marginTop: "1rem",
          overflowX: "auto",
          borderRadius: "10px",
          boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        }}
      >
        <table
          className="reference-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "1rem",
            backgroundColor: "#fff",
          }}
        >
          <thead style={{ background: "#0ea5e9", color: "white" }}>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: "0.8rem 1rem",
                  width: "25%",
                }}
              >
                Keyword
              </th>
              <th style={{ textAlign: "left", padding: "0.8rem 1rem" }}>
                Description
              </th>
            </tr>
          </thead>
          <tbody>
            {futureReserved.map((item, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "#f9fafb" : "#fff",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <td
                  style={{
                    padding: "0.7rem 1rem",
                    fontFamily: "monospace",
                    fontWeight: "bold",
                    color: "#2563eb",
                  }}
                >
                  {item.name}
                </td>
                <td style={{ padding: "0.7rem 1rem" }}>{item.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
